/**
 * Atto Generator page — Legal Pro killer feature.
 *
 * Two tabs:
 *   - "Nuovo atto"   → wizard (pick → form → generating → done)
 *   - "I miei atti"  → personal archive of every previously generated act
 *
 * Tier-gated to legal_pro/business — others see the upsell card.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  FileSignature,
  Scale,
  Gavel,
  ShieldAlert,
  BookOpen,
  Mail,
  XCircle,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  Lock,
  Copy,
  Printer,
  BookmarkPlus,
  FolderOpen,
  Trash2,
  Search as SearchIcon,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { PublishToMarketplaceModal } from "@/components/PublishToMarketplaceModal";

const ATTO_ICONS = {
  memoria_difensiva: Scale,
  ricorso_cassazione: Gavel,
  istanza_sospensione: ShieldAlert,
  parere_legale: BookOpen,
  diffida: Mail,
  lettera_recesso: XCircle,
};

const ALLOWED = ["legal_pro", "business"];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Atti() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);

  // Top-level tab: "create" runs the wizard; "archive" shows the personal list.
  const [tab, setTab] = useState("create");

  // Wizard state
  const [step, setStep] = useState("pick"); // pick | form | generating | done
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    parties: "",
    subject: "",
    facts: "",
    court: "",
    additional_context: "",
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [savingToKB, setSavingToKB] = useState(false);

  // Archive state
  const [archive, setArchive] = useState({ items: [], total: 0, loading: false, error: null });
  const [archiveQuery, setArchiveQuery] = useState("");
  const [archiveTypeFilter, setArchiveTypeFilter] = useState("");
  const [archiveOpened, setArchiveOpened] = useState(null); // full doc when user opens an item
  const [publishOpen, setPublishOpen] = useState(false);

  const allowed = user && ALLOWED.includes(user.subscription_tier);

  useImperativeLoad(() => {
    if (!user) return;
    api.get("/legal/atti/catalog").then((r) => {
      setCatalog(r.data.atti || []);
    });
  }, [user?.user_id]);

  const fetchArchive = useCallback(async () => {
    setArchive((a) => ({ ...a, loading: true, error: null }));
    try {
      const params = {};
      if (archiveQuery.trim()) params.q = archiveQuery.trim();
      if (archiveTypeFilter) params.atto_type = archiveTypeFilter;
      const r = await api.get("/legal/atti/archive", { params });
      setArchive({ items: r.data.items || [], total: r.data.total || 0, loading: false, error: null });
    } catch (e) {
      setArchive({ items: [], total: 0, loading: false, error: t("atti.archive_load_error") });
    }
  }, [archiveQuery, archiveTypeFilter, t]);

  // Load the archive whenever the user switches to that tab or changes filters.
  useImperativeLoad(() => {
    if (!allowed) return;
    if (tab !== "archive") return;
    fetchArchive();
  }, [allowed, tab, archiveQuery, archiveTypeFilter]);

  const handlePick = (atto) => {
    setSelected(atto);
    setStep("form");
  };

  const handleGenerate = async () => {
    if (!selected) return;
    if (!form.parties.trim() || !form.subject.trim() || !form.facts.trim()) {
      toast.error(t("atti.err_required"));
      return;
    }
    setGenerating(true);
    setStep("generating");
    try {
      const r = await api.post("/legal/atti/generate", {
        atto_type: selected.id,
        parties: form.parties.trim(),
        subject: form.subject.trim(),
        facts: form.facts.trim(),
        court: form.court.trim() || null,
        additional_context: form.additional_context.trim() || null,
        kb_auto_include: true,
      }, { timeout: 90000 });
      setResult(r.data);
      setStep("done");
      toast.success(t("atti.toast_generated"));
    } catch (err) {
      toast.error(t("atti.err_generation"));
      setStep("form");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (text) => {
    const body = text || result?.document || archiveOpened?.document;
    if (!body) return;
    try {
      await navigator.clipboard.writeText(body);
      toast.success(t("atti.toast_copied"));
    } catch (e) {
      toast.error(t("atti.err_copy"));
    }
  };

  const handlePrint = () => window.print();

  const handleSaveToKB = async () => {
    const doc = result?.document || archiveOpened?.document;
    const sub = form.subject || archiveOpened?.subject || "";
    const label = (selected?.label_it) || archiveOpened?.label || "Atto";
    const typeId = (selected?.id) || archiveOpened?.atto_type || "atto";
    if (!doc) return;
    setSavingToKB(true);
    try {
      await api.post("/legal/knowledge", {
        title: `${label} — ${sub.slice(0, 80)}`,
        body: doc,
        source: "manual",
        citation: `NEURACOM Atto Generator — ${label}`,
        tags: ["atto", typeId],
      });
      toast.success(t("atti.toast_saved_kb"));
    } catch (e) {
      toast.error(t("atti.err_save_kb"));
    } finally {
      setSavingToKB(false);
    }
  };

  const handleReset = () => {
    setStep("pick");
    setSelected(null);
    setResult(null);
    setForm({ parties: "", subject: "", facts: "", court: "", additional_context: "" });
  };

  const handleOpenArchived = async (item) => {
    try {
      const r = await api.get(`/legal/atti/archive/${item.atto_id}`);
      setArchiveOpened(r.data);
    } catch (e) {
      toast.error(t("atti.archive_load_error"));
    }
  };

  const handleDeleteArchived = async (item) => {
    if (!window.confirm(t("atti.archive_delete_confirm"))) return;
    try {
      await api.delete(`/legal/atti/archive/${item.atto_id}`);
      toast.success(t("atti.archive_delete_success"));
      // Optimistic local removal + total decrement
      setArchive((a) => ({
        ...a,
        items: a.items.filter((i) => i.atto_id !== item.atto_id),
        total: Math.max(0, a.total - 1),
      }));
    } catch (e) {
      toast.error(t("atti.archive_load_error"));
    }
  };

  const archiveCountLabel = useMemo(() => {
    const key = archive.total === 1 ? "atti.archive_count_one" : "atti.archive_count_many";
    return t(key).replace("{count}", String(archive.total));
  }, [archive.total, t]);

  if (!allowed) {
    return (
      <div className="h-full overflow-y-auto bg-[#0A0C1F]" data-testid="atti-upsell">
        <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
          <div className="rounded-3xl border border-[#D4A937]/30 bg-gradient-to-br from-[#0B0E5C] to-[#05073B] p-10 text-center shadow-xl">
            <Lock className="mx-auto h-10 w-10 text-[#D4A937]" />
            <h1 className="mt-4 font-display text-3xl font-extrabold text-white">
              {t("atti.upsell_title")}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
              {t("atti.upsell_body")}
            </p>
            <Link
              to="/pricing"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4A937] px-6 py-3 text-sm font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="atti-upsell-cta"
            >
              <Scale className="h-4 w-4" />
              {t("atti.upsell_cta")}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // ────────────── ARCHIVE: opened detail view ──────────────
  if (tab === "archive" && archiveOpened) {
    const sections = archiveOpened.sections || [];
    return (
      <div className="h-full overflow-y-auto bg-[#0A0C1F] print:bg-white" data-testid="atti-archive-detail">
        <section className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
          <button
            type="button"
            onClick={() => setArchiveOpened(null)}
            className="mb-6 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white print:hidden"
            data-testid="atti-archive-back"
          >
            <ArrowLeft className="h-3 w-3" /> {t("atti.archive_open_back")}
          </button>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">
                {archiveOpened.label}
              </p>
              <p className="font-mono text-xs text-slate-500">
                {formatDate(archiveOpened.created_at)} · {archiveOpened.char_count} {t("atti.chars")} · {archiveOpened.model_used}
                {archiveOpened.kb_notes_used?.length > 0 && ` · ${archiveOpened.kb_notes_used.length} ${t("atti.kb_used")}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setPublishOpen(true)}
                size="sm"
                className="rounded-full bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="atti-archive-publish-btn"
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {t("publish_from_atti.button")}
              </Button>
              <Button
                onClick={() => handleCopy(archiveOpened.document)}
                size="sm" variant="outline"
                className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                data-testid="atti-archive-copy-btn"
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("atti.copy")}
              </Button>
              <Button
                onClick={handlePrint}
                size="sm" variant="outline"
                className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                data-testid="atti-archive-print-btn"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" /> {t("atti.print")}
              </Button>
              <Button
                onClick={handleSaveToKB}
                disabled={savingToKB}
                size="sm"
                className="rounded-full bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="atti-archive-save-kb-btn"
              >
                {savingToKB ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                  <>
                    <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" /> {t("atti.save_to_kb")}
                  </>
                )}
              </Button>
            </div>
          </div>
          {sections.length > 0 && (
            <p className="mb-4 font-mono text-[10px] uppercase tracking-wider text-slate-500 print:hidden">
              {sections.join(" · ")}
            </p>
          )}
          <Card className="border-slate-800 bg-white p-10 text-[#1a1a1a] shadow-2xl print:border-0 print:shadow-none">
            <pre
              className="whitespace-pre-wrap font-serif text-[14px] leading-[1.7] text-[#1a1a1a]"
              data-testid="atti-archive-document"
            >
              {archiveOpened.document}
            </pre>
          </Card>
          <PublishToMarketplaceModal
            open={publishOpen}
            onOpenChange={setPublishOpen}
            atto={archiveOpened}
          />
        </section>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#0A0C1F] print:bg-white" data-testid="atti-page">
      <section className="mx-auto max-w-5xl px-6 py-10 lg:px-10">
        {/* Header */}
        <div className="mb-6 print:hidden">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#F4C955]">
            <FileSignature className="h-3 w-3" />
            {t("atti.pretitle")}
          </span>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {t("atti.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("atti.subtitle")}</p>
        </div>

        {/* Tab switcher */}
        <div className="mb-8 flex items-center gap-1 border-b border-slate-800 print:hidden" data-testid="atti-tabs">
          <button
            type="button"
            onClick={() => { setTab("create"); setArchiveOpened(null); }}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              tab === "create"
                ? "border-[#D4A937] text-white"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
            data-testid="atti-tab-create"
          >
            <Plus className="h-4 w-4" />
            {t("atti.tab_create")}
          </button>
          <button
            type="button"
            onClick={() => setTab("archive")}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
              tab === "archive"
                ? "border-[#D4A937] text-white"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
            data-testid="atti-tab-archive"
          >
            <FolderOpen className="h-4 w-4" />
            {t("atti.tab_archive")}
            {archive.total > 0 && (
              <span className="ml-1 rounded-full bg-[#D4A937]/20 px-2 py-0.5 text-[10px] font-bold text-[#F4C955]">
                {archive.total}
              </span>
            )}
          </button>
        </div>

        {/* ─────────── CREATE TAB (wizard) ─────────── */}
        {tab === "create" && step === "pick" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 print:hidden" data-testid="atti-catalog">
            {catalog.map((atto) => {
              const Icon = ATTO_ICONS[atto.id] || FileSignature;
              return (
                <Card
                  key={atto.id}
                  className="group cursor-pointer border-slate-800 bg-slate-900/60 p-5 transition-all hover:-translate-y-1 hover:border-[#D4A937]/40 hover:bg-slate-900"
                  onClick={() => handlePick(atto)}
                  data-testid={`atti-card-${atto.id}`}
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4A937]/15 text-[#F4C955]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-base font-bold text-white">
                    {atto.label_it}
                  </h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    {atto.sections.length} {t("atti.sections_count")}: {atto.sections.join(" · ")}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold text-[#D4A937] opacity-0 transition-opacity group-hover:opacity-100">
                    {t("atti.start")} <ChevronRight className="h-3 w-3" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {tab === "create" && step === "form" && selected && (
          <Card className="border-slate-800 bg-slate-900/60 p-6 print:hidden" data-testid="atti-form">
            <button
              type="button"
              onClick={() => setStep("pick")}
              className="mb-4 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
              data-testid="atti-form-back"
            >
              <ArrowLeft className="h-3 w-3" /> {t("atti.back")}
            </button>
            <h2 className="font-display text-2xl font-bold text-white">{selected.label_it}</h2>
            <p className="mt-1 text-sm text-slate-400">
              {t("atti.form_hint", { sections: selected.sections.join(", ") })}
            </p>

            <div className="mt-6 space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("atti.field_parties")} *
                </label>
                <Input
                  value={form.parties}
                  onChange={(e) => setForm((f) => ({ ...f, parties: e.target.value }))}
                  placeholder={t("atti.field_parties_ph")}
                  className="border-slate-700 bg-slate-900 text-white"
                  data-testid="atti-form-parties"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("atti.field_subject")} *
                </label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder={t("atti.field_subject_ph")}
                  className="border-slate-700 bg-slate-900 text-white"
                  data-testid="atti-form-subject"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("atti.field_facts")} *
                </label>
                <Textarea
                  value={form.facts}
                  onChange={(e) => setForm((f) => ({ ...f, facts: e.target.value }))}
                  placeholder={t("atti.field_facts_ph")}
                  rows={8}
                  className="border-slate-700 bg-slate-900 text-white"
                  data-testid="atti-form-facts"
                />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t("atti.field_court")}
                  </label>
                  <Input
                    value={form.court}
                    onChange={(e) => setForm((f) => ({ ...f, court: e.target.value }))}
                    placeholder={t("atti.field_court_ph")}
                    className="border-slate-700 bg-slate-900 text-white"
                    data-testid="atti-form-court"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t("atti.field_context")}
                  </label>
                  <Input
                    value={form.additional_context}
                    onChange={(e) => setForm((f) => ({ ...f, additional_context: e.target.value }))}
                    placeholder={t("atti.field_context_ph")}
                    className="border-slate-700 bg-slate-900 text-white"
                    data-testid="atti-form-context"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="mt-6 w-full rounded-full bg-[#D4A937] py-6 text-base font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="atti-form-generate"
            >
              {generating ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  {t("atti.generate_cta")}
                </>
              )}
            </Button>
            <p className="mt-3 text-center text-[10px] text-slate-500">
              {t("atti.kb_hint")}
            </p>
          </Card>
        )}

        {tab === "create" && step === "generating" && (
          <Card className="flex flex-col items-center justify-center border-slate-800 bg-slate-900/60 p-16 print:hidden" data-testid="atti-generating">
            <div className="relative">
              <div className="absolute inset-0 animate-pulse rounded-full bg-[#D4A937]/20 blur-xl" />
              <Sparkles className="relative h-12 w-12 animate-spin text-[#D4A937]" />
            </div>
            <p className="mt-6 font-display text-xl font-bold text-white">
              {t("atti.generating_title")}
            </p>
            <p className="mt-2 text-sm text-slate-400">{t("atti.generating_hint")}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#D4A937]">
              Claude Opus 4.8 · ~45–60s
            </p>
          </Card>
        )}

        {tab === "create" && step === "done" && result && (
          <div className="space-y-4" data-testid="atti-result">
            <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">
                  {result.label}
                </p>
                <p className="font-mono text-xs text-slate-500">
                  {result.char_count} {t("atti.chars")} · {result.model_used}
                  {result.kb_notes_used?.length > 0 && ` · ${result.kb_notes_used.length} ${t("atti.kb_used")}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleCopy()}
                  size="sm" variant="outline"
                  className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                  data-testid="atti-copy-btn"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("atti.copy")}
                </Button>
                <Button
                  onClick={handlePrint}
                  size="sm" variant="outline"
                  className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                  data-testid="atti-print-btn"
                >
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> {t("atti.print")}
                </Button>
                <Button
                  onClick={handleSaveToKB}
                  disabled={savingToKB}
                  size="sm"
                  className="rounded-full bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="atti-save-kb-btn"
                >
                  {savingToKB ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                    <>
                      <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" /> {t("atti.save_to_kb")}
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleReset}
                  size="sm" variant="ghost"
                  className="rounded-full text-slate-400 hover:bg-slate-800 hover:text-white"
                  data-testid="atti-new-btn"
                >
                  {t("atti.new_atto")}
                </Button>
              </div>
            </div>

            <Card className="border-slate-800 bg-white p-10 text-[#1a1a1a] shadow-2xl print:border-0 print:shadow-none">
              <pre className="whitespace-pre-wrap font-serif text-[14px] leading-[1.7] text-[#1a1a1a]" data-testid="atti-document">
                {result.document}
              </pre>
            </Card>
          </div>
        )}

        {/* ─────────── ARCHIVE TAB ─────────── */}
        {tab === "archive" && (
          <div className="print:hidden" data-testid="atti-archive">
            {/* Search + filter chips */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  value={archiveQuery}
                  onChange={(e) => setArchiveQuery(e.target.value)}
                  placeholder={t("atti.archive_search_ph")}
                  className="border-slate-700 bg-slate-900 pl-9 text-white"
                  data-testid="atti-archive-search"
                />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="atti-archive-count">
                {archiveCountLabel}
              </p>
            </div>
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setArchiveTypeFilter("")}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                  archiveTypeFilter === ""
                    ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]"
                    : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
                }`}
                data-testid="atti-archive-filter-all"
              >
                {t("atti.archive_filter_all")}
              </button>
              {catalog.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setArchiveTypeFilter(c.id)}
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                    archiveTypeFilter === c.id
                      ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]"
                      : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
                  }`}
                  data-testid={`atti-archive-filter-${c.id}`}
                >
                  {c.label_it}
                </button>
              ))}
            </div>

            {archive.loading && (
              <p className="text-center text-sm text-slate-500" data-testid="atti-archive-loading">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> {t("atti.archive_loading")}
              </p>
            )}
            {archive.error && (
              <p className="text-center text-sm text-rose-400" data-testid="atti-archive-error">{archive.error}</p>
            )}

            {!archive.loading && !archive.error && archive.items.length === 0 && (
              <Card className="border-slate-800 bg-slate-900/60 p-12 text-center" data-testid="atti-archive-empty">
                <FolderOpen className="mx-auto h-10 w-10 text-slate-600" />
                <h3 className="mt-3 font-display text-lg font-bold text-white">
                  {t("atti.archive_empty_title")}
                </h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
                  {t("atti.archive_empty_body")}
                </p>
                <Button
                  onClick={() => setTab("create")}
                  className="mt-6 rounded-full bg-[#D4A937] font-bold text-[#05073B] hover:bg-[#E0B850]"
                  data-testid="atti-archive-empty-cta"
                >
                  <Sparkles className="mr-2 h-4 w-4" /> {t("atti.archive_empty_cta")}
                </Button>
              </Card>
            )}

            {!archive.loading && !archive.error && archive.items.length > 0 && (
              <div className="space-y-3" data-testid="atti-archive-list">
                {archive.items.map((item) => {
                  const Icon = ATTO_ICONS[item.atto_type] || FileSignature;
                  return (
                    <Card
                      key={item.atto_id}
                      className="flex flex-col gap-4 border-slate-800 bg-slate-900/60 p-5 transition-colors hover:border-[#D4A937]/40 sm:flex-row sm:items-start"
                      data-testid={`atti-archive-item-${item.atto_id}`}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#D4A937]/15 text-[#F4C955]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">
                          {item.label}
                        </p>
                        <h3 className="mt-0.5 truncate font-display text-base font-bold text-white">
                          {item.subject || "—"}
                        </h3>
                        {item.parties && (
                          <p className="mt-0.5 truncate text-sm text-slate-400">{item.parties}</p>
                        )}
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                          {formatDate(item.created_at)} · {item.char_count} {t("atti.chars")}
                          {item.kb_notes_used?.length > 0 && ` · ${item.kb_notes_used.length} ${t("atti.kb_used")}`}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          onClick={() => handleOpenArchived(item)}
                          size="sm" variant="outline"
                          className="rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                          data-testid={`atti-archive-view-${item.atto_id}`}
                        >
                          <FolderOpen className="mr-1.5 h-3.5 w-3.5" /> {t("atti.archive_view")}
                        </Button>
                        <Button
                          onClick={() => handleDeleteArchived(item)}
                          size="sm" variant="ghost"
                          className="rounded-full text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
                          data-testid={`atti-archive-delete-${item.atto_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
